import { useEffect, useMemo, useRef, useState } from 'react'
import {
  api,
  type QueueBranch,
  type QueueRow,
  type QueueStageValue,
  type StagePositions,
} from '../lib/api.ts'
import {
  STAGE_LABEL,
  STATUS_LABEL,
  STATUS_ORDER,
  STATUS_TONE,
  isAnalysing,
  label,
  stageOf,
} from '../lib/vocabulary.ts'
import { Button } from '../components/Button.tsx'
import { Input } from '../components/Input.tsx'
import { TableSkeleton } from '../components/TableSkeleton.tsx'
import { TableError, TableOverlay, TableStatus } from '../components/DataTable.tsx'
import { useToast } from '../components/Toast.tsx'
import { Checkbox } from '../components/Checkbox.tsx'
import { Progress } from '../components/Progress.tsx'
import { Pagination } from '../components/Pagination.tsx'
import { FOCUS_RING } from '../components/focusRing.ts'
import { icons } from '../components/icons.ts'
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { titleCaseName } from '../lib/titleCase.ts'
import { DispositionMark } from '../components/ComparisonCard.tsx'
import { SyncQuotation } from '../components/SyncQuotation.tsx'
import { totalPages } from '../lib/pagination.ts'
import { MultiSelect } from '../components/MultiSelect.tsx'
import { StatusBadge } from '../components/StatusBadge.tsx'
import { useResizableColumns, type ResizableColumns } from '../components/useResizableColumns.ts'
import { Tooltip } from '../components/Tooltip.tsx'
import { useQueueFilters } from '../lib/queueFilters.ts'
import { QUEUE_STAGES, stagesToAsk } from '../lib/queueStages.ts'
import { canReverify, reverifyRefusal } from '../lib/reverifiable.ts'
import { reverifyEach } from '../lib/reverifyEach.ts'
import { rowRefresh } from '../lib/rowRefresh.ts'
import { useAutoRefresh } from '../lib/useAutoRefresh.ts'
import { DateRange, FUTURE_PRESETS, PAST_PRESETS } from '../components/DateRange.tsx'

/**
 * The queue a pharmacist works.
 *
 * V1's chrome, because they already scan this list every day and the fastest
 * queue is the one nobody has to re-learn: the patient's name leads, status is
 * its own column, the filters carry their counts, and branch and search sit
 * together on the right.
 *
 * **The patient's name is here**, reversing V2's own earlier stance that a
 * queue is a list of work and picking your next item should not need a
 * screenful of other people's names. V1 leads with it and people scan by it,
 * and matching how they already work beat the argument.
 */

const PAGE_SIZE = 20

/**
 * The filters, in V1's order, each with a dot where it has a tone.
 *
 * `Em análise` is ours: V1 has no admission, so it never had verifications sitting at
 * a checkpoint for minutes with nothing wrong.
 */
/**
 * The bench stages, in the order a requisição passes through them.
 *
 * There is deliberately no "em andamento" option any more. That was a
 * combination wearing a stage's clothes, invented when this filter could only
 * hold one value; with a set it is just the two stages somebody would tick,
 * which is what the default now is.
 */
const STAGES = QUEUE_STAGES.map((value) => ({ value, label: label(STAGE_LABEL, value) }))

/**
 * Every status a person can narrow to, coloured as the badges are.
 *
 * Derived from `STATUS_ORDER` rather than listed again. The copy that used to
 * live here drifted the moment a status was added: `discarded` had a label and
 * a tone and was simply missing from the filter, so the only way to find one
 * was to know it existed and scroll.
 */
const STATUSES = STATUS_ORDER.map((value) => ({
  value,
  label: label(STATUS_LABEL, value),
  tone: STATUS_TONE[value]?.dot,
}))

type SortColumn = 'patient_name' | 'branch_code' | 'quotation_number' | 'keyed_at' | 'pickup_at'
/** Status ahead of the column, either way, or the column on its own. */
type StatusSort = 'asc' | 'desc' | 'off'

/** The two ERP dates the queue can be windowed on. */
type DateField = 'pickup_at' | 'keyed_at'
const DATE_LABEL: Record<DateField, string> = { pickup_at: 'Entrega', keyed_at: 'Criada' }

/*
 * V1's table chrome, so a row here reads like a row there.
 *
 * The body is deliberately *not* the filters' type, but it is not larger
 * either: V1 runs its headings and chips at 14/600 and its cells at 14/400,
 * so the difference between chrome and content is weight rather than size.
 * The patient's name is `text-tP` and **not** semibold, exactly as V1 has it:
 * every name in bold is no emphasis at all.
 */
/*
 * Sticky, so the column a row belongs to is still readable a hundred rows
 * down. It needs its own background -- a transparent sticky header lets the
 * rows scroll visibly underneath it.
 *
 * `z-10` is only ever meant to beat the rows, and the container carries
 * `isolate` so that is all it can ever do. Without it the header competes in
 * the page's own stacking context and outranks anything floating over it that
 * happens to come earlier in the document -- which is how the account menu
 * ended up opening behind a table.
 */
const TH =
  'sticky top-0 z-10 border-b border-stroke-weak bg-background-sunken py-3 text-tPSemi text-text-strong'
const CELL = 'px-3'
// `align-middle`, so the selection box lines up with the row's text rather
// than with the top of the tallest cell in it.
/*
 * `overflow-hidden`, and every cell means it.
 *
 * `table-fixed` gives each column the width it was told and *not* the width its
 * content needs, so a long patient name or a wide badge simply drew past its
 * own boundary and over the column beside it. The layout was already correct;
 * nothing had been told to stop.
 *
 * `align-middle` because the selection box was otherwise levelling with the
 * top of the tallest cell rather than with the row's text.
 */
const TD = 'border-b border-stroke-weak py-3 text-tP text-text-strong align-middle overflow-hidden'

export function Queue({
  onOpen,
  search: searchFromHeader = '',
  visibleStages,
  visibleStatuses,
  stagePositions,
}: {
  onOpen: (row: { id: string; branch: number; quotation: number; simulation: boolean }) => void
  /** Set by the header's search box, the one search there is. */
  search?: string
  /**
   * The stages this person's role reaches (#280), or undefined until the
   * session has said. Only those are offered; the server applies the same
   * scope whatever is asked, so this only spares somebody a dead option.
   */
  visibleStages?: readonly string[] | undefined
  /**
   * The statuses this person's role reaches (#439), or undefined until the
   * session has said. The status filter offers only those; the server scopes
   * the list whatever is asked.
   */
  visibleStatuses?: readonly string[] | undefined
  /**
   * Where the account's stages start on its FC12540 (#673), or undefined
   * until the session has said. A row's stage is not drawn before then: the
   * same position is a different stage at another pharmacy.
   */
  stagePositions?: StagePositions | undefined
}) {
  const [rows, setRows] = useState<QueueRow[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  // Everything, by default. The queue opens on the whole day's work and the
  // chips narrow it -- landing on a filter means landing on a partial view
  // that looks like the whole one.
  // Remembered, both of them: somebody picks a filter once and works inside it
  // for an afternoon.
  /*
   * In the URL, and on the account.
   *
   * A filtered queue is a thing people send each other, so it has to be in the
   * address; and a person is one person, so the browser is the wrong place to
   * remember what they chose. The URL wins when it says anything, the saved
   * preference fills in when it does not.
   */
  const filters = useQueueFilters()
  const stage = filters.values['stage'] ?? ''
  const status = filters.values['status'] ?? ''
  // A string, so the request below reruns when the answer changes and not
  // whenever the role's list is handed down again.
  const askedStages = stagesToAsk(stage, visibleStages)
  const setStage = (next: string) => filters.set('stage', next)
  const setStatus = (next: string) => filters.set('status', next)
  const branch = filters.values['branch'] ?? ''
  const setBranch = (next: string) => filters.set('branch', next)
  const [search, setSearch] = useState(searchFromHeader)
  /*
   * A number searched for, when that is what was typed.
   *
   * The box takes a number, a barcode or a patient's name, and only the first
   * two can be fetched from the ERP by asking for them. A name that found
   * nothing is a name that found nothing.
   */
  // A scan's thirteen digits are not a quotation number; the server says
  // which filial and number they encode.
  const [scanned, setScanned] = useState<{ branchCode: number; quotationNumber: number }>()
  const searchedNumber =
    scanned?.quotationNumber ?? (/^\d+$/.test(search.trim()) ? Number(search.trim()) : undefined)
  /*
   * Earliest promised pickup first: the order the physical bench works in,
   * as Galdino asked. Our own "atualizada" said when *we* last touched a
   * row, which is nothing a pharmacist prioritises by.
   */
  const [sort, setSort] = useState<SortColumn>('pickup_at')
  const [dir, setDir] = useState<'asc' | 'desc'>('asc')
  /*
   * Status leads the column sort: what a person can act on now comes first,
   * and within that band the column decides. Three states rather than two,
   * because the third -- the column alone -- is the one the other headers
   * already offer, and a status sort nobody can switch off is a fourth
   * filter in disguise.
   */
  const [statusDir, setStatusDir] = useState<StatusSort>('asc')
  /*
   * A window of days on one of the ERP's dates. Local to the screen rather
   * than saved with the other filters: "entregas de hoje" is today's
   * question, and a saved window would greet somebody tomorrow with an empty
   * queue and no visible reason.
   */
  const [dateField, setDateField] = useState<DateField>('pickup_at')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)
  const [error, setError] = useState<string | undefined>()
  const toast = useToast()
  // Bumped by "tentar de novo" so the effect below runs again, in the foreground.
  const [attempt, setAttempt] = useState(0)

  /*
   * Two axes, not one.
   *
   * Whether an verification is *running* is a status; whether it has anything flagged
   * is a property of its findings. "Sem apontamentos" needs both -- finished,
   * and with nothing on it -- and asking only for the status showed every
   * finished verification including the ones full of divergences.
   */
  /*
   * The quality chips are gone.
   *
   * "Todos / Com divergência / Em análise / Sem apontamentos" asked a question
   * the stage and status filters now answer better — and one of them was
   * actively harmful: "com divergência" reads as a worklist, and a pharmacist is
   * required to check every requisição, not only the ones we already suspect.
   * A filter that quietly narrows the job is worse than no filter.
   */

  /*
   * Refreshed while anything on screen is still running.
   *
   * One request for the whole page, not one per row: fifty rows in flight is
   * the same information at fifty times the cost, the replies would land at
   * fifty different moments, and a row that *finishes* has to leave whatever
   * filter it was in -- which only a list request knows.
   *
   * It stops the moment nothing is running. A queue of finished work polls
   * nothing at all.
   */
  const anyRunning = useMemo(() => rows.some((r) => isAnalysing(r.status)), [rows])
  const auto = useAutoRefresh(anyRunning)

  useEffect(() => {
    // Nothing until the account has answered: querying with the defaults
    // first shows a queue the person did not ask for and then replaces it,
    // which reads as the screen changing its mind.
    if (!filters.ready) return undefined

    let cancelled = false
    /*
     * A background refresh does not say "carregando".
     *
     * Every poll used to blank the table to a loading row and paint it again,
     * so a queue with anything running flickered the whole screen every three
     * seconds -- for news that usually amounted to nothing. Only a change
     * somebody made (a filter, a sort, a page) is worth showing a wait for.
     */
    const background = auto.takeBackground()
    if (!background) setLoading(true)

    api
      .queue({
        stage: askedStages,
        // The chip narrows by what is *on* an verification; this narrows by what
        // status it is in. Different questions, so both can be asked at once.
        ...(status === '' ? {} : { status: status }),
        branch,
        q: search,
        sort,
        dir,
        ...(statusDir === 'off' ? {} : { status_dir: statusDir }),
        ...(from === '' && to === '' ? {} : { date: dateField, from, to }),
        limit: PAGE_SIZE,
        offset,
      })
      .then((page) => {
        if (cancelled) return
        /*
         * Replaced only when it actually differs.
         *
         * React re-renders every row given a new array, identical contents or
         * not -- and most polls carry identical contents. Holding the previous
         * array keeps the rows untouched, which also keeps anything living
         * inside them: an open divergences panel survives a poll instead of
         * being torn down under the pointer.
         */
        setRows((previous) =>
          sameRows(previous, page.verifications) ? previous : page.verifications,
        )
        setTotal(page.total)
        setScanned(page.scanned ?? undefined)
        if (page.branches !== undefined) setBranches(page.branches)
        setError(undefined)
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : 'falha ao carregar')
      })
      .finally(() => {
        if (!cancelled && !background) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [
    filters.ready,
    askedStages,
    status,
    branch,
    search,
    sort,
    dir,
    statusDir,
    dateField,
    from,
    to,
    offset,
    auto.token,
    attempt,
  ])

  // The header can hand us a term at any time, including while the queue is
  // already open.
  useEffect(() => {
    setSearch(searchFromHeader)
    setOffset(0)
  }, [searchFromHeader])

  /*
   * Widths a person has dragged, remembered per browser.
   *
   * The queue is the densest screen here and every pharmacy reads it
   * differently -- one cares about long patient names, another about seeing
   * four filiais at once. A fixed layout serves the average of those and
   * nobody in particular.
   */
  const columns = useResizableColumns('queue')

  /*
   * From the server, not from the rows.
   *
   * Derived from the page, narrowing to one filial removed every other from
   * the list -- so there was no way to add a second, and no way back except
   * clearing. A filter that eats its own options is a trap.
   */
  const [branches, setBranches] = useState<readonly QueueBranch[]>([])

  /**
   * Runs the selected verifications again.
   *
   * The same request the verification screen's "Atualizar comparação" makes, once per
   * selection -- so a batch and a single are the same operation, and a failed
   * one gets a fresh workflow rather than a signal into nothing.
   *
   * Sequential on purpose. Each of these starts model work; firing fifty at
   * once would hand the worker a spike that admission exists to prevent.
   */
  /**
   * One row's reverify, from the icon on "Aguardando correção".
   *
   * Refreshed quietly afterwards: the row changes status under the pointer, and
   * blanking the table to say so would hide the thing somebody just acted on.
   */
  async function reverifyOne(id: string) {
    setError(undefined)
    try {
      await api.reverify(id)
      auto.markBackground()
      auto.refresh()
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao reconferir', { tone: 'error' })
    }
  }

  async function updateSelected() {
    const asked = selected.size
    setUpdating(true)
    setError(undefined)
    const { failed, reason } = await reverifyEach(selected, (id) => api.reverify(id))

    /*
     * Only the refused stay selected.
     *
     * The ones that went through are being reconferidas now, and a bar still
     * counting them invites a second press that buys a second identical answer
     * at full price. The refused stay because they are the ones somebody has
     * to look at -- and their checkbox keeps working, which is why the row
     * only refuses a tick it does not already hold.
     */
    setSelected(new Set(failed))

    if (failed.length > 0) {
      toast.show(
        failed.length === asked
          ? (reason ?? 'falha ao atualizar')
          : `${failed.length} de ${asked} não foram atualizadas — ${reason ?? 'falha ao atualizar'}`,
        { tone: 'error' },
      )
    }

    // Quietly: the bar already says what is happening, and blanking the
    // table under it would hide the rows somebody just acted on.
    auto.markBackground()
    auto.refresh()
    setUpdating(false)
  }

  /**
   * Ticks a row, or unticks it.
   *
   * A row that cannot be reverified can still be *unticked* -- it may have been
   * selected while it could, and been refused since. What it cannot do is join
   * a selection it is not already in, which is the same refusal the route
   * would give it.
   */
  function toggle(id: string, status: string) {
    if (!selected.has(id) && !canReverify(status)) return
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  /** asc, then desc, then the column alone. */
  function cycleStatusSort() {
    setStatusDir(statusDir === 'asc' ? 'desc' : statusDir === 'desc' ? 'off' : 'asc')
    setOffset(0)
  }

  function sortBy(column: SortColumn) {
    if (sort === column) setDir(dir === 'asc' ? 'desc' : 'asc')
    else {
      setSort(column)
      // Names read forwards; everything else newest or highest first.
      setDir(column === 'patient_name' ? 'asc' : 'desc')
    }
    setOffset(0)
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {/* The tabs carry the heading in V1; ours has no stage split, so the
          title stays for screen readers and off the screen. */}
      <h1 className="sr-only">Fila de conferência</h1>

      {/*
        Both groups pinned to the control height rather than trusting
        `items-center`. The chips' wrapping container was measuring 52px tall
        while every chip in it was 32 -- a phantom flex line -- so centring
        against it pushed the branch and search ten pixels below the row they
        belong on.
      */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div
          className="flex h-8 flex-nowrap items-center gap-2"
          role="group"
          aria-label="Filtrar a fila"
        >
          {/*
            The bench stage, before the quality filters.

            Two different questions, in the order they are asked: *where* is
            this work, then *what* is wrong with it. Kept visually apart by a
            divider so they do not read as one row of five interchangeable
            chips.
          */}
          <MultiSelect
            label="Filial"
            options={branches.map((b) => ({
              value: String(b.code),
              label: `${b.name} (${b.code})`,
            }))}
            searchable
            selected={branch === '' ? [] : branch.split(',')}
            onChange={(next) => {
              setBranch(next.join(','))
              setOffset(0)
            }}
            allLabel="Todas"
          />
          <MultiSelect
            label="Etapa"
            options={
              visibleStages === undefined
                ? STAGES
                : STAGES.filter((one) => visibleStages.includes(one.value))
            }
            selected={stage === '' ? [] : stage.split(',')}
            onChange={(next) => {
              setStage(next.join(','))
              setOffset(0)
            }}
          />
          <MultiSelect
            label="Status"
            options={
              visibleStatuses === undefined
                ? STATUSES
                : STATUSES.filter((one) => visibleStatuses.includes(one.value))
            }
            selected={status === '' ? [] : status.split(',')}
            onChange={(next) => {
              setStatus(next.join(','))
              setOffset(0)
            }}
          />
          <span aria-hidden="true" className="bg-stroke-weak mx-1 h-5 w-px shrink-0" />
          {/* A window of days on one of the two ERP dates, apart from the
              quality filters: *when*, after *where* and *what*. */}
          <DateRange
            label={DATE_LABEL[dateField]}
            // A promised pickup is ahead; a keyed date is behind.
            presets={dateField === 'pickup_at' ? FUTURE_PRESETS : PAST_PRESETS}
            value={{ from, to }}
            onChange={(next) => {
              setFrom(next.from)
              setTo(next.to)
              setOffset(0)
            }}
            header={
              /* Which date the window is on. A pair of tabs rather than a
                 dropdown: two options, both worth seeing. */
              <div
                role="tablist"
                aria-label="Data"
                className="bg-fill-weak flex w-fit gap-0.5 rounded-[6px] p-0.5"
              >
                {(Object.keys(DATE_LABEL) as DateField[]).map((field) => (
                  <button
                    key={field}
                    type="button"
                    role="tab"
                    aria-selected={dateField === field}
                    className={`text-tSmall rounded-[4px] px-2.5 py-1 ${FOCUS_RING} ${
                      dateField === field
                        ? 'bg-fill-white text-text-strong shadow-sm'
                        : 'text-text-weak hover:text-text-strong'
                    }`}
                    onClick={() => {
                      setDateField(field)
                      setOffset(0)
                    }}
                  >
                    {DATE_LABEL[field]}
                  </button>
                ))}
              </div>
            }
          />
        </div>

        {/* Search on the same row as the filters, as V1 has it.
            `flex-nowrap` and `shrink-0`, because with four chips on the left
            this group was being squeezed until its two controls wrapped onto
            separate lines -- reading as two unrelated things and costing a row
            at the top of the densest screen in the product. The outer row
            wraps instead, which puts the pair on their own line intact. */}
        <div className="flex min-w-[16rem] flex-1 items-center gap-2">
          {/* The one search there is. Number, barcode or patient, in the
              room the row has left. */}
          <Input
            size="small"
            type="search"
            name="fila"
            autoComplete="off"
            data-1p-ignore
            data-lpignore="true"
            data-form-type="other"
            leadingIcon={faMagnifyingGlass}
            placeholder="Buscar por orçamento, código de barras ou paciente"
            aria-label="Buscar na fila"
            containerClassName="min-w-0 flex-1"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setOffset(0)
            }}
          />
        </div>
      </div>

      {selected.size > 0 && (
        /*
         * V1's bar, and only what V1 puts in it: the count on the left, one
         * action on the right. There is no "limpar" -- the header checkbox
         * clears a selection, and a second button beside the one that matters
         * only makes somebody read both before pressing either.
         */
        <div
          data-selection-bar
          className="border-stroke-brand-strong bg-fill-brand-weak rounded-base flex min-h-14 w-full items-center justify-between border py-2 pl-3 pr-2"
        >
          <span className="text-tP text-text-weak">
            {selected.size} de {total} selecionadas
          </span>
          <Button
            variant="primary"
            tone="brand"
            size="small"
            leadingIcon={icons.refresh}
            disabled={updating}
            onClick={updateSelected}
            title="Roda a conferência de novo para cada uma das selecionadas"
          >
            {updating ? 'Atualizando…' : `Atualizar ${selected.size}`}
          </Button>
        </div>
      )}

      {/* The table scrolls, the pagination below it does not. A queue is
          worked from the top down, and having to scroll past forty rows to
          reach the next page is the one interaction it has. */}
      <div className="rounded-base border-stroke-weak bg-background-raised isolate relative min-h-0 w-full flex-1 overflow-auto border">
        {/* `select-none` while dragging: without it, pulling a boundary
            highlights every cell it passes over. */}
        <table
          className={`w-full table-fixed border-collapse ${columns.resizing ? 'select-none' : ''}`}
        >
          <thead>
            <tr>
              <th scope="col" className={`${TH} w-[56px]`}>
                <span className="sr-only">Selecionar</span>
              </th>
              {/* Filial first: the code somebody says, then the name -- wide
                  enough for "50 · TELE ARTPHARMA" on one line, and fixed so
                  the column does not breathe as the page changes. */}
              <SortableHeader
                column="branch_code"
                label="Filial"
                width="w-[168px]"
                sort={sort}
                dir={dir}
                onSort={sortBy}
                columns={columns}
              />
              {/* The number somebody quotes on the phone, next to the counter
                  it belongs to. */}
              <SortableHeader
                column="quotation_number"
                label="Orçamento"
                width="w-[118px]"
                sort={sort}
                dir={dir}
                onSort={sortBy}
                columns={columns}
              />
              {/* Where it is, beside the number it belongs to and before the
                  name — the two identifiers first, then the status of the work,
                  then who it is for. */}
              <th
                scope="col"
                className={`${TH} ${CELL} relative overflow-hidden text-left whitespace-nowrap`}
                style={{ width: columns.widthOf('etapa') ?? 132 }}
              >
                <span className="block truncate pr-2">Etapa</span>
                <ResizeHandle column="etapa" columns={columns} />
              </th>
              {/*
                The one column with no width.

                `table-fixed` divides what is left over among the columns that
                did not ask for a size, so leaving this one alone is what makes
                it take the slack — and a patient's name is the only thing here
                that is genuinely variable-length. Everything else is a code, a
                badge or a date, and giving those room they do not need is what
                pushed names into an ellipsis on a wide screen.
              */}
              <SortableHeader
                column="patient_name"
                label="Paciente"
                sort={sort}
                dir={dir}
                onSort={sortBy}
                columns={columns}
                fill
                nextColumn="status"
              />
              {/* Sortable, but not like the others: the status leads whatever
                  column is sorted, in the order a pharmacist works the queue
                  (waiting for a person, running, waiting for a slot, waiting on the ERP,
                  over). The third click hands the order back to the column. */}
              <th
                scope="col"
                aria-sort={
                  statusDir === 'off' ? 'none' : statusDir === 'asc' ? 'ascending' : 'descending'
                }
                className={`${TH} ${CELL} relative overflow-hidden text-left whitespace-nowrap`}
                style={{ width: columns.widthOf('status') ?? 176 }}
              >
                <button
                  type="button"
                  onClick={cycleStatusSort}
                  title={
                    statusDir === 'off'
                      ? 'Ordenar por status primeiro'
                      : statusDir === 'asc'
                        ? 'Status primeiro: a conferir no topo'
                        : 'Status primeiro: concluídas no topo'
                  }
                  className={`rounded-base flex w-full min-w-0 items-center gap-1 overflow-hidden pr-2 ${FOCUS_RING}`}
                >
                  <span className="truncate">Status</span>
                  <span
                    aria-hidden="true"
                    className={`shrink-0 ${statusDir === 'off' ? 'text-icon-neutral opacity-40' : 'text-icon-brand'}`}
                  >
                    {statusDir === 'off' ? '↕' : statusDir === 'asc' ? '↑' : '↓'}
                  </span>
                </button>
                <ResizeHandle column="status" columns={columns} />
              </th>
              {/*
                How much of the work is done, as a fraction.
                
                "Apontamentos" is the product's own word for these, and the
                fraction says the two things a person wants at once: whether
                anything is wrong, and whether anybody has dealt with it. The
                exclamation said only the first.
              */}
              <th
                scope="col"
                className={`${TH} ${CELL} relative overflow-hidden text-left whitespace-nowrap`}
                style={{ width: columns.widthOf('apontamentos') ?? 132 }}
              >
                <span className="block truncate pr-2">Apontamentos</span>
                <ResizeHandle column="apontamentos" columns={columns} />
              </th>
              {/* The ERP's two dates, as V1 had them: when it was keyed and
                  when the pharmacy promised it. Ours -- "atualizada" -- said
                  when we last touched the row, which nobody works by. */}
              <SortableHeader
                column="keyed_at"
                label="Criada"
                width="w-[112px]"
                sort={sort}
                dir={dir}
                onSort={sortBy}
                columns={columns}
              />
              <SortableHeader
                column="pickup_at"
                label="Entrega"
                width="w-[112px]"
                sort={sort}
                dir={dir}
                onSort={sortBy}
                columns={columns}
              />
            </tr>
          </thead>
          <tbody>
            {/*
              Rows of the right shape, not a single centred word: the table
              keeps its height while a page loads, so the pager somebody just
              clicked does not jump out from under the cursor.
            */}
            {loading && rows.length === 0 && <TableSkeleton columns={8} rows={PAGE_SIZE} />}

            {/* Rows stay while a later page or a poll arrives -- faded, with a
                spinner over them -- instead of blinking out to a skeleton. */}
            {rows.length > 0 &&
              rows.map((row) => (
                <tr
                  key={row.id}
                  className={`hover:bg-fill-weak transition-colors ${
                    selected.has(row.id) ? 'bg-fill-brand-weak' : ''
                  } ${loading ? 'opacity-40' : ''}`}
                >
                  <td className={`${TD} text-center`}>
                    <SelectCell
                      row={row}
                      selected={selected.has(row.id)}
                      onToggle={() => toggle(row.id, row.status)}
                    />
                  </td>
                  {/* "50 · TELE ARTPHARMA": the code somebody says, then the
                      name, on one line; the rest is on hover. */}
                  <td className={`${TD} ${CELL}`}>
                    <span
                      className="block truncate whitespace-nowrap"
                      title={`${row.branch_code} · ${row.branch_name ?? ''}`.trim()}
                    >
                      <span className="tabular-nums">{row.branch_code}</span>
                      {row.branch_name !== undefined &&
                        row.branch_name !== String(row.branch_code) && (
                          <span className="text-text-weak"> · {row.branch_name}</span>
                        )}
                    </span>
                  </td>
                  <td className={`${TD} ${CELL} tabular-nums`}>
                    <span className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate">#{row.quotation_number}</span>
                      {row.simulation && (
                        /* Beside the number, because that is what was invented:
                           the prescription is a real page somebody uploaded and
                           the quotation is the part we made up. */
                        <Tooltip text="Simulação — orçamento inventado a partir de uma receita enviada, não lido do ERP.">
                          <span className="text-icon-warning inline-flex shrink-0 items-center">
                            <FontAwesomeIcon icon={icons.simulation} className="text-xs" />
                          </span>
                        </Tooltip>
                      )}
                    </span>
                  </td>
                  <td className={`${TD} ${CELL}`}>
                    <StageCell stage={stageOfRow(row, stagePositions)} />
                  </td>
                  <td className={`${TD} ${CELL}`}>
                    <span className="flex min-w-0 items-center gap-2">
                      <button
                        type="button"
                        className={`text-text-brand text-tP rounded-base min-w-0 truncate text-left ${FOCUS_RING}`}
                        onClick={() =>
                          onOpen({
                            id: row.id,
                            branch: row.branch_code,
                            quotation: row.quotation_number,
                            simulation: row.simulation,
                          })
                        }
                      >
                        {titleCaseName(row.patient_name ?? undefined) ||
                          'Paciente não identificado'}
                        {/* An orçamento for more than one person (#342). */}
                        {(row.patient_count ?? 0) > 1 && (
                          <span className="text-text-weak"> +{(row.patient_count ?? 0) - 1}</span>
                        )}
                      </button>
                    </span>
                  </td>
                  <td className={`${TD} ${CELL}`}>
                    <StatusCell
                      row={row}
                      stage={stageOfRow(row, stagePositions)}
                      onReverify={reverifyOne}
                    />
                  </td>
                  <td className={`${TD} ${CELL}`}>
                    <FindingsCell row={row} />
                  </td>
                  <td className={`${TD} ${CELL} text-text-weak tabular-nums`}>
                    {dayAndTime(row.keyed_at)}
                  </td>
                  <td className={`${TD} ${CELL} text-text-weak tabular-nums`}>
                    {dayAndTime(row.pickup_at)}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        {!loading && error !== undefined && rows.length === 0 && (
          <TableStatus>
            <TableError message={error} onRetry={() => setAttempt((n) => n + 1)} />
          </TableStatus>
        )}
        {!loading && error === undefined && rows.length === 0 && (
          <TableStatus>
            {/* A number searched for and not found is the one empty result
                that has an answer: discovery reads forward from a watermark,
                so an orçamento it has already passed can never arrive on its
                own, and asking an engineer was the only way in. */}
            {visibleStages !== undefined && visibleStages.length === 0 ? (
              // Said, rather than an empty queue that reads as a quiet day.
              <span className="text-tP text-text-weak">
                Sua conta ainda não tem uma função nesta farmácia. Peça a um administrador.
              </span>
            ) : searchedNumber === undefined ? (
              <span className="text-tP text-text-weak">Nada aqui.</span>
            ) : (
              <SyncQuotation
                // Keyed so each new scan fetches afresh.
                key={
                  scanned === undefined
                    ? 'typed'
                    : `${scanned.branchCode}-${scanned.quotationNumber}`
                }
                quotationNumber={searchedNumber}
                branchCode={scanned?.branchCode}
                onOpened={(found) => onOpen({ ...found, simulation: false })}
              />
            )}
          </TableStatus>
        )}
        {loading && rows.length > 0 && <TableOverlay />}
      </div>

      <Pagination
        currentPage={Math.floor(offset / PAGE_SIZE) + 1}
        totalPages={totalPages(total, PAGE_SIZE)}
        onPageChange={(page: number) => setOffset((page - 1) * PAGE_SIZE)}
      />
    </div>
  )
}

/**
 * Whether two pages of the queue say the same thing.
 *
 * Only the fields the table draws. Comparing whole objects would call every
 * poll a change -- `updated_at` moves when anything at all touches the row --
 * and comparing nothing would redraw on every poll regardless.
 */
function sameRows(a: readonly QueueRow[], b: readonly QueueRow[]): boolean {
  if (a.length !== b.length) return false
  return a.every((row, i) => {
    const other = b[i]!
    return (
      row.id === other.id &&
      row.status === other.status &&
      row.step === other.step &&
      row.patient_name === other.patient_name &&
      row.divergence === other.divergence &&
      row.undecided === other.undecided &&
      row.reviewed === other.reviewed &&
      row.updated_at === other.updated_at
    )
  })
}

/** Everything on a row that still needs a person. */
function flaggedCount(row: QueueRow): number {
  return row.divergence + row.undecided
}

/** Whether this row is mid-analysis, and so worth asking about again. */

function SortableHeader({
  column,
  label: text,
  width,
  sort,
  dir,
  onSort,
  columns,
  fill = false,
  nextColumn,
}: {
  column: SortColumn
  label: string
  width?: string
  sort: SortColumn
  dir: 'asc' | 'desc'
  onSort: (column: SortColumn) => void
  /** Passed so the header can be dragged wider. */
  columns?: ResizableColumns
  /**
   * The column that takes the slack. It carries no width and no drag -- a
   * drag would set a width that is then ignored -- but it still draws the
   * line at its right edge, or the boundary before the next column vanishes.
   */
  fill?: boolean
  /** With `fill`: the column whose left edge the handle moves. */
  nextColumn?: string
}) {
  const on = sort === column
  const dragged = fill ? undefined : columns?.widthOf(column)
  return (
    <th
      scope="col"
      aria-sort={on ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={`${TH} ${CELL} ${dragged === undefined ? (width ?? '') : ''} relative overflow-hidden text-left`}
      style={dragged === undefined ? undefined : { width: dragged }}
    >
      {/* Clipped inside the cell and kept clear of the handle at its right
          edge, so a narrow column truncates its label instead of running the
          sort arrow through the separator. */}
      <button
        type="button"
        onClick={() => onSort(column)}
        className={`rounded-base flex w-full min-w-0 items-center gap-1 overflow-hidden pr-2 ${FOCUS_RING}`}
      >
        <span className="truncate">{text}</span>
        <span
          aria-hidden="true"
          className={`shrink-0 ${on ? 'text-icon-brand' : 'text-icon-neutral opacity-40'}`}
        >
          {on ? (dir === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </button>
      {columns !== undefined && <ResizeHandle column={column} columns={columns} />}
      {/* The boundary after the fill column is dragged from the *next*
          column's left edge: this column has no width to change. */}
      {fill && columns !== undefined && nextColumn !== undefined && (
        <ResizeHandle column={nextColumn} columns={columns} edge="left" />
      )}
    </th>
  )
}

/**
 * The seam between two headers, and the thing you drag.
 *
 * Visible at rest rather than only on hover: a handle nobody can see is a
 * feature nobody finds, and the line doubles as the answer to "where does this
 * column end" — which is the question somebody is asking right before they
 * reach for it.
 *
 * Wider than it looks. The line is one pixel and the target is eight, because
 * a one-pixel target is a fair description of something that cannot be
 * grabbed.
 */
function ResizeHandle({
  column,
  columns,
  edge = 'right',
}: {
  column: string
  columns: ResizableColumns
  edge?: 'left' | 'right'
}) {
  return (
    <span
      role="separator"
      aria-orientation="vertical"
      aria-label="Redimensionar coluna"
      onPointerDown={(event) => columns.onGrab(column, event, edge)}
      // The header is a sort button; a drag starting here must not also sort.
      onClick={(event) => event.stopPropagation()}
      className="absolute top-1/2 right-0 z-10 h-5 w-2 -translate-y-1/2 cursor-col-resize touch-none"
    >
      <span aria-hidden="true" className="bg-stroke-weak block h-full w-px" />
    </span>
  )
}

/**
 * The tick that puts a row in the batch, and says why when it will not.
 *
 * "Atualizar N" is one reverify per selected row, so a row the route would
 * refuse has no business being selectable: the request spends a round trip
 * learning what the status already said, and before this the whole batch died
 * on it. Refused rows say so on hover rather than sitting there greyed and
 * unexplained.
 */
function SelectCell({
  row,
  selected,
  onToggle,
}: {
  row: QueueRow
  selected: boolean
  onToggle: () => void
}) {
  // Never on a row already ticked: whoever ticked it must be able to untick it,
  // whatever the status has done since.
  const refusal = selected ? undefined : reverifyRefusal(row.status)
  const box = (
    <Checkbox
      checked={selected}
      disabled={refusal !== undefined}
      onChange={onToggle}
      aria-label={`Selecionar requisição ${row.quotation_number}`}
    />
  )
  // Wrapped, not titled: a disabled input fires no hover of its own.
  return refusal === undefined ? (
    box
  ) : (
    <Tooltip text={`Não pode ser reconferida agora. ${refusal}`}>
      <span className="inline-flex">{box}</span>
    </Tooltip>
  )
}

/** A row's stage on the account's positions, or undefined until the session has said them. */
function stageOfRow(
  row: QueueRow,
  positions: StagePositions | undefined,
): QueueStageValue | undefined {
  return positions === undefined ? undefined : stageOf(row.stage_pos ?? 0, positions)
}

/**
 * What is happening to this verification.
 *
 * V1 says a count of errors, or "análise incompleta", or nothing at all. Ours
 * says the same three things in V2's words, and adds the checkpoint -- because
 * under admission an verification can sit at one for a long time by design, and
 * "Lendo" alone cannot tell working from stuck. V1 had nothing here to add
 * because V1 had no steps.
 */
/**
 * Where the pharmacy has the requisição, and whether anything is wrong with it.
 *
 * The exclamation lives here rather than in the status column because it is
 * about the *work*, not about the stage: a requisição in produção with three
 * divergences is still worth flagging, and the status column is already
 * answering a different question.
 */
function StageCell({ stage }: { stage: QueueStageValue | undefined }) {
  if (stage === undefined) return null

  return (
    <span className="flex min-w-0 items-center gap-1.5">
      {/*
        A badge, and a neutral one.

        It reads as a badge because the status beside it does, and two
        differently-shaped labels in adjacent columns look like two different
        kinds of fact. Uncoloured because the stage is not a decision: where the
        pharmacy has put a requisição is not good or bad, and a palette here
        would compete with the one column that is saying something urgent.
      */}
      {/* `min-w-0` and `truncate`: a badge that refuses to shrink pushes its
          own cell open from the inside, which is the same overflow by another
          route. */}
      <span className="bg-fill-weak text-tSmall text-text-weak inline-flex min-w-0 items-center rounded-full px-2 py-[2px]">
        <span className="truncate">{STAGE_LABEL[stage]}</span>
      </span>
    </span>
  )
}

/**
 * One badge, coloured by whose turn it is.
 *
 * Not by severity: what somebody scanning the queue wants is "is this mine",
 * and a palette that ranks statuses by how bad they are answers a question
 * nobody asked. Ours is brand, a pharmacist's is red, somebody else's is
 * amber, finished is green.
 *
 * An analysis in flight keeps its progress line, above the badge rather than
 * instead of it — the badge says what status it is in and the line says how far
 * through, which are two facts and were being collapsed into one.
 */
function StatusCell({
  row,
  stage,
  onReverify,
}: {
  row: QueueRow
  stage: QueueStageValue | undefined
  onReverify: (id: string) => void
}) {
  const refresh = rowRefresh(row.status, stage)
  /*
   * No falling back to the status.
   *
   * `row.step ?? row.status` fed a status into the strip whenever a run had no
   * checkpoint, and the strip labels checkpoints -- so `waiting` drew
   * "Aguardando" in the badge and "Na fila" under it, one fact told twice in
   * two vocabularies, and `concluding` had no checkpoint label at all and
   * printed the raw key.
   *
   * The step is null exactly when nothing is running, which is already the
   * question the strip is asking, so the checkpoint alone decides it.
   */
  const step = row.step

  return (
    <span className="flex min-w-0 flex-col gap-1">
      {/* `min-w-0` on the row too: a flex child defaults to its content's
          width and refuses to shrink below it, so an email address held this
          open regardless of what the cell said. */}
      <span className="flex min-w-0 items-center gap-2">
        <StatusBadge status={row.status} reason={row.failure_reason} />
        {/*
          The one status that is waiting on somebody outside this screen.
          
          "Aguardando correção" says what it is waiting for and not what to do
          about it, and the thing to do -- come back and refresh once the ERP
          has changed -- is a click nobody would guess belongs to a row. The
          icon is the affordance; the tooltip is the instruction.
        */}
        {/*
          And sem receita before produção, which waits on the pharmacy in the
          same way. Which rows, and in what colour, is `rowRefresh`'s answer.
        */}
        {refresh !== undefined && (
          <Tooltip text={refresh.tooltip}>
            <button
              type="button"
              aria-label={refresh.label}
              style={{ color: refresh.color }}
              className={`inline-flex size-4 shrink-0 items-center justify-center ${FOCUS_RING}`}
              onClick={(event) => {
                // The row opens the conferência; this asks for a reverify.
                event.stopPropagation()
                onReverify(row.id)
              }}
            >
              <FontAwesomeIcon icon={icons.refresh} aria-hidden="true" className="text-[11px]" />
            </button>
          </Tooltip>
        )}
        {/*
          An icon, not the address.
          
          "Com emannuel@quave.com.br" spent the width of the column on a string
          nobody reads twice, and pushed the badge it belongs to out of line. Who
          is on it matters when you are deciding whether to open it -- which is a
          question you ask about one row, not about forty at a glance.
        */}
        {row.assigned_to != null && (
          <Tooltip text={`${row.assigned_to} está conferindo`}>
            <span
              className="text-icon-weak inline-flex size-4 shrink-0 items-center justify-center"
              aria-label={`${row.assigned_to} está conferindo`}
            >
              <FontAwesomeIcon icon={icons.user} aria-hidden="true" className="text-[11px]" />
            </span>
          </Tooltip>
        )}
      </span>
      {/*
        Under the badge, not over it.

        The badge is what the row *is* and the strip is detail about it, so the
        badge should be what the eye lands on when scanning a column of them.
        Above, the strip pushed every running row's badge out of line with the
        rest of the column.
      */}
      {step != null && <Progress step={step} compact />}
      {/*
        Back from the correction the pharmacist asked for, and waiting on
        their final check (#439). Said in words: it is the one row in the
        column that is here a second time, and why is the whole point.
      */}
      {row.returned_from_correction === true && (
        <span className="text-tSmall text-text-information truncate">Voltou da correção</span>
      )}
    </span>
  )
}

/**
 * How much of the work is done, as a fraction.
 *
 * The exclamation said only that something was wrong. What a person choosing
 * their next item wants is both halves at once: whether anything is wrong, and
 * whether anybody has dealt with it — a row at 4/5 is nearly finished and a row
 * at 0/5 has not been touched, and the mark drew them identically.
 *
 * **Both decisions count as answered.** ✓ and ✗ are a person having looked,
 * which is what the fraction measures; what they concluded is the conferência's
 * business, not the queue's.
 *
 * A conferência with nothing to answer says so in words rather than "0/0",
 * which reads as a fraction that failed to load.
 */
function FindingsCell({ row }: { row: QueueRow }) {
  const total = flaggedCount(row)
  // A dash, not a sentence: the column is a number everywhere else.
  if (total === 0) return <span className="text-tP text-text-weak">—</span>

  const done = Math.min(row.flagged_reviewed ?? 0, total)
  return <FlaggedBadge row={row} count={total} done={done} />
}

/**
 * How many, and — on a press — which.
 *
 * "3 divergências" does not distinguish three wrong doses from three
 * rewordings, and choosing what to work next is the queue's whole job. The
 * previews come down with the row, so opening this costs nothing and closing
 * it loses nothing.
 */
function FlaggedBadge({ row, count, done }: { row: QueueRow; count: number; done: number }) {
  const [open, setOpen] = useState(false)
  const [at, setAt] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const trigger = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)

  /*
   * Opened into the top layer.
   *
   * The table scrolls horizontally, and anything absolutely positioned inside
   * a scroll container is clipped by it -- the panel was cut off and pushed
   * the table sideways instead of covering it. `popover` puts it above the
   * page entirely, so no ancestor's overflow can reach it, and its position is
   * measured from the button rather than inherited.
   */
  useEffect(() => {
    const element = panel.current
    if (element === null) return undefined

    if (!open) {
      if (element.matches(':popover-open')) element.hidePopover()
      return undefined
    }

    const box = trigger.current?.getBoundingClientRect()
    if (box !== undefined) {
      // Flipped above the button when there is no room below, so a row near
      // the bottom of a long queue does not open off-screen.
      const below = window.innerHeight - box.bottom
      setAt({
        top: below < 240 ? Math.max(8, box.top - 8 - 240) : box.bottom + 4,
        left: Math.min(box.left, window.innerWidth - 396),
      })
    }
    element.showPopover()

    const away = (event: MouseEvent) => {
      if (
        !element.contains(event.target as Node) &&
        !trigger.current?.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    // Closed on scroll as well: the panel is positioned once, and a page that
    // moves under it would leave it pointing at a different row.
    const moved = () => setOpen(false)

    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', escape)
    window.addEventListener('scroll', moved, true)
    return () => {
      document.removeEventListener('mousedown', away)
      document.removeEventListener('keydown', escape)
      window.removeEventListener('scroll', moved, true)
    }
  }, [open])

  return (
    <>
      {/* Ours, not the browser's: `title` waits a second, cannot be styled and
          never appears for a keyboard user. */}
      <Tooltip
        text={`${done} de ${count} ${
          count === 1 ? 'apontamento respondido' : 'apontamentos respondidos'
        } — clique para ver quais`}
      >
        <button
          ref={trigger}
          type="button"
          aria-expanded={open}
          aria-label={`${done} de ${count} ${count === 1 ? 'apontamento respondido' : 'apontamentos respondidos'}, ver quais`}
          onClick={(event) => {
            // The row opens the verification; this opens the list. Without stopping it
            // here, asking what is wrong navigates away from the answer.
            event.stopPropagation()
            setOpen((v) => !v)
          }}
          /*
           * An icon, not a sentence.
           *
           * "4 divergências" spent the width of a column to say something the
           * list behind it says better -- and said it next to a stage, where a
           * reader is scanning for where the work is rather than reading counts.
           * The mark says *something is wrong here*; pressing it says what.
           *
           * The count survives in the label, so anybody who cannot see the icon
           * still gets the number.
           */
          className={`text-tP rounded-base inline-flex items-center gap-1.5 tabular-nums ${
            done >= count ? 'text-text-success' : 'text-text-error'
          } ${FOCUS_RING}`}
        >
          {/* Finished reads as finished. A row whose apontamentos are all
              answered has nothing left to ask of anybody, and painting it the
              same red as an untouched one is the queue shouting at work that
              is done. */}
          <FontAwesomeIcon
            icon={done >= count ? icons.ok : icons.alert}
            aria-hidden="true"
            className="text-[11px]"
          />
          {done}/{count}
        </button>
      </Tooltip>

      <div
        ref={panel}
        popover="manual"
        role="dialog"
        aria-label="Divergências"
        style={{ top: at.top, left: at.left }}
        className="rounded-base border-stroke-weak bg-background-raised fixed m-0 w-[380px] max-w-[70vw] border p-3 shadow-lg"
      >
        <span className="text-tTiny text-text-weak mb-2 block uppercase tracking-[0.04em]">
          Divergências
        </span>
        {row.previews.length === 0 ? (
          /* Flagged, but analysed before the messages existed. "Nada divergiu"
             would be a lie about a row we have just called flagged. */
          <span className="text-tSmall text-text-weak block">
            Esta conferência é anterior aos textos dos achados. Abra-a para ver.
          </span>
        ) : (
          <ul className="flex list-none flex-col gap-1 p-0">
            {row.previews.map((line, index) => (
              <li key={index} className="text-tSmall text-text-strong flex items-start gap-1.5">
                {/* The same shape the conferência draws for the same finding. A
                    pharmacist meets a finding here first and again there, and
                    two marks for one thing is how they stop trusting either. */}
                <span className="pt-[0.15rem]">
                  <DispositionMark disposition={line.disposition} answered={false} />
                </span>
                <span className="min-w-0">{line.text}</span>
              </li>
            ))}
          </ul>
        )}
        {count > row.previews.length && row.previews.length > 0 && (
          <span className="text-tSmall text-text-weak mt-2 block">
            e mais {count - row.previews.length}. Abra a conferência para ver todas.
          </span>
        )}
      </div>
    </>
  )
}

/** `05/09 12:41`, or a dash for a row discovery has not dated yet. */
function dayAndTime(iso: string | null | undefined): string {
  if (iso === undefined || iso === null) return '—'
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return '—'
  return at.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}
